import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import { ApiError, fetchAvailableUnits, saveIngredientUnits } from '@/api'
import { Ingredient, IngredientUnit, TempIngredientUnit } from '@/api/types'
import { Label } from '@/components/Label'
import { Spinner } from '@/components/Spinner'
import ServingSizeItem from './ServingSizesItem'

import './ServingSizes.css'

type Props = {
  ingredient: Ingredient
  isDisabled?: boolean
}

type MeasurementUnit = TempIngredientUnit | IngredientUnit

export default function ServingSizes({ ingredient, isDisabled }: Props) {
  const { t } = useTranslation()
  const [tempUnits, setTempUnits] = useState<MeasurementUnit[]>(ingredient.units ?? [])
  const [error, setError] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['availableUnits'],
    queryFn: fetchAvailableUnits,
    staleTime: Infinity,
  })

  const saveMutation = useMutation({
    mutationFn: (updatedUnits: MeasurementUnit[]) =>
      saveIngredientUnits(ingredient.idIngredient, updatedUnits),
    onSuccess: (res) => {
      setTempUnits(res)
    },
    onError: (e: ApiError) => {
      if (e.response.data.code === 'UNIT_IN_USE') {
        setError(true)
      } else {
        toast.error('There was an error deleting the unit')
      }
    },
  })

  if (isLoading) {
    return <Spinner />
  }

  if (data === undefined) {
    return null
  }

  const unitsOptions = data.map((unit) => ({
    value: unit,
    label: t(`measurementUnits.${unit}` as any),
  }))

  const handleAddUnit = (ev: React.MouseEvent<HTMLButtonElement>) => {
    ev.preventDefault()

    const unitTemplate = {
      name: undefined,
      quantityInGrams: 1,
    } as TempIngredientUnit

    setTempUnits([...tempUnits, unitTemplate])
    setError(false)
  }

  const handleUpdate = (item: MeasurementUnit) => {
    const updatedList = tempUnits.map((unit) => {
      if (unit.idUnit === item.idUnit) {
        return item
      }
      return unit
    })

    setTempUnits(updatedList)
    setError(false)

    saveMutation.mutate(updatedList)
  }

  const handleRemove = (idUnit: number) => {
    const updatedList = tempUnits.filter((item) => item.idUnit !== idUnit)
    saveMutation.mutate(updatedList)

    setError(false)
  }

  const hasTempUnit = tempUnits.some((u) => u.idUnit === undefined)

  return (
    <div className="panel-xs" style={{ gap: 8 }}>
      <Label>{t('ingredient.servingSize')}</Label>

      <div>
        <div className="ServingSizes-grid ServingSizes-header">
          <span>{t('ingredient.unit')}</span>
          <span>{t('ingredient.grams')}</span>
          {!isDisabled && (
            <div>
              <button onClick={handleAddUnit} className="ServingSizes-add" disabled={hasTempUnit}>
                <FontAwesomeIcon icon={faPlus} width={10} height={10} color="var(--gray-500)" />
              </button>
            </div>
          )}
        </div>

        <div className="ServingSizes-list">
          {tempUnits.length > 0 ? (
            <>
              {tempUnits.map((item, index) => (
                <ServingSizeItem
                  key={item.idUnit + index}
                  isDisabled={isDisabled}
                  item={item}
                  units={tempUnits}
                  availableUnits={unitsOptions}
                  onUpdate={handleUpdate}
                  onRemove={handleRemove}
                />
              ))}
            </>
          ) : (
            <div className="ServingSizes-emptyText">{t('ingredient.noUnits')}</div>
          )}
          {error && <div className="ServingSizes-error">{t('ingredient.errorDeleteUnit')}</div>}
        </div>
      </div>
    </div>
  )
}
